import React, { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import matter from 'gray-matter'
// Utility to generate slug IDs from heading text
function slugify(text) {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

import { useLocation } from 'react-router-dom'
export default function Help() {
  const [content, setContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const location = useLocation()

  useEffect(() => {
    const fetchDoc = async () => {
      try {
        const res = await fetch('/help_document.md')
        if (!res.ok) throw new Error('Failed to load help documentation')
        const raw = await res.text()
        const cleaned = raw
          .replace(/<!--([\s\S]*?)-->/g, '')
          .replace(/\s*\{#[^}]+\}/g, '')
        const parsed = matter(cleaned)
        setContent(parsed.content)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchDoc()
  }, [])

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1)
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.hash, content])

  const showScreenshots = import.meta.env.HELP_SCREENSHOTS
  if (loading) return <p className="p-4">Loading help...</p>
  if (error) return <p className="p-4 text-error">Error: {error}</p>

  return (
    <article className="help-doc mx-auto p-6 max-w-[75ch]">
      <h1 className="text-4xl font-bold mb-6">Help Documentation</h1>
      <div className="markdown-body">
        <ReactMarkdown
          components={{
            h1: ({ node, ...props }) => {
              const text = Array.isArray(props.children) ? props.children.join('') : ''
              return (
                <h1 id={slugify(text)} className="text-4xl font-bold mt-8 mb-4" {...props} />
              )
            },
            h2: ({ node, ...props }) => {
              const text = Array.isArray(props.children) ? props.children.join('') : ''
              return (
                <h2 id={slugify(text)} className="text-2xl font-semibold mt-6 mb-3" {...props} />
              )
            },
            h3: ({ node, ...props }) => {
              const text = Array.isArray(props.children) ? props.children.join('') : ''
              return (
                <h3 id={slugify(text)} className="text-xl font-semibold mt-4 mb-2" {...props} />
              )
            },
            p: ({ node, ...props }) => <p className="text-base leading-relaxed mb-4" {...props} />, 
            ul: ({ node, ...props }) => <ul className="list-disc list-inside mb-4 ml-4" {...props} />, 
            ol: ({ node, ...props }) => <ol className="list-decimal list-inside mb-4 ml-4" {...props} />, 
            blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-accent pl-4 italic my-4 text-muted" {...props} />, 
            a: ({ node, ...props }) => {
              const href = props.href || ''
              if (href.startsWith('#')) {
                const id = href.slice(1)
                return (
                  <a
                    {...props}
                    className="text-accent hover:underline cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault()
                      const el = document.getElementById(id)
                      if (el) el.scrollIntoView({ behavior: 'smooth' })
                    }}
                  />
                )
              }
              return <a className="text-accent hover:underline" {...props} />
            },
            img: ({ node, ...props }) =>
              showScreenshots ? <img {...props} /> : null
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    </article>
  )
}